import React, { useMemo, useState } from 'react';
import { fmtMeasure, unitOf } from './measureFormat';

/**
 * ActivityTable — the COA activities grid, shared by the Create tab (work
 * plan) and Precision COA.
 *
 * Design rules:
 *   - The FULL CMS universe layout (columns A–L) is visible on the row —
 *     the universe is the product; nothing essential hides behind "View".
 *   - Every row is ONE compact line. Prose columns truncate to a single
 *     line with the full text on hover and in the expansion panel.
 *   - Wide content scrolls horizontally INSIDE the grid container; a row
 *     never grows tall or takes over the screen.
 *   - Deficiencies and Result read TBD until the row is scored (synced or
 *     keyed). After scoring: the real number, and the rate vs benchmark
 *     with a Met/Missed pill.
 *   - Clicking a row (or its chevron) expands the long fields at readable
 *     width underneath.
 */

function cmsDate(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.replace(/-/g, '/') : s;
}

function StatusChip({ act }) {
  const isAgent = !!act.generated_by_agent;
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${act.status === 'completed' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : act.status === 'expired' ? 'bg-slate-100 text-slate-500 ring-1 ring-slate-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}
        title={act.status === 'expired' && act.expire_reason ? `Expired${act.expired_by ? ` by ${act.expired_by}` : ''}: ${act.expire_reason}` : undefined}
      >
        {act.status === 'completed' ? 'closed' : (act.status || 'draft')}
      </span>
      {isAgent && (
        <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700 ring-1 ring-violet-200 whitespace-nowrap" title="Drafted by the monthly oversight agent for your review">
          agent
        </span>
      )}
    </div>
  );
}

function DefBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-slate-400 text-xs font-medium">TBD</span>;
  const n = Number(value);
  if (n > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 tabular-nums" title={`${n} deficiencies`}>
        <span aria-hidden>&#9888;</span>{n}
      </span>
    );
  }
  return <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 tabular-nums">0</span>;
}

/**
 * Result: TBD until the row is scored. Scored monitoring rows show the rate
 * against the benchmark and a Met/Missed pill. Audit rows with case metrics
 * keep the drill-in chip.
 */
function ResultCell({ act, onOpen }) {
  const scored = ['met', 'missed'].includes(act.monitoring_status);
  const s = act.metrics_summary;
  if (scored) {
    // A result reads in the unit its benchmark was set in: "2:00" for a 120
    // second call-center standard, "95%" for a timeliness rate.
    const unit = unitOf(act);
    const rate = s && s.rate !== null && s.rate !== undefined ? Number(s.rate) : null;
    const benchRaw = act.series_benchmark ?? act.benchmark;
    const bench = benchRaw !== null && benchRaw !== undefined ? Number(benchRaw) : null;
    const met = act.monitoring_status === 'met';
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen && onOpen({ id: act.id, name: `${act.component || 'Activity'}${act.entity_name ? ` — ${act.entity_name}` : ''}` }); }}
        disabled={!onOpen}
        className="text-left group disabled:cursor-default"
        title="View the scored case types"
      >
        <span className={`block text-xs font-semibold tabular-nums whitespace-nowrap ${met ? 'text-emerald-700' : 'text-rose-700'} ${onOpen ? 'group-hover:underline' : ''}`}>
          {rate !== null ? fmtMeasure(rate, unit) : (met ? 'Met' : 'Missed')}
          {bench !== null && <span className="text-slate-400 font-normal"> vs {fmtMeasure(bench, unit)}</span>}
        </span>
        <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${met ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'}`}>
          {met ? 'Met' : 'Missed'}
        </span>
      </button>
    );
  }
  if (s && s.count) {
    const rate = s.rate === null || s.rate === undefined ? null : Number(s.rate);
    const tone = s.below_target > 0 ? 'text-rose-700' : s.deficiencies > 0 ? 'text-amber-700' : 'text-emerald-700';
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen && onOpen({ id: act.id, name: `${act.component || 'Activity'}${act.entity_name ? ` — ${act.entity_name}` : ''}` }); }}
        disabled={!onOpen}
        className="text-left group disabled:cursor-default"
        title="View each case type and set a target"
      >
        <span className={`block text-xs font-semibold tabular-nums ${tone} ${onOpen ? 'group-hover:underline' : ''}`}>
          {rate === null ? 'no cases' : fmtMeasure(rate, unitOf(act))}
        </span>
        <span className="block text-[10px] text-slate-500">
          {s.count} type{s.count === 1 ? '' : 's'}{s.deficiencies > 0 ? `, ${s.deficiencies} late` : ''}
        </span>
      </button>
    );
  }
  // Plan rows that will score later read TBD; free-standing audit rows a dash.
  return act.monitoring_series_id
    ? <span className="text-slate-400 text-xs font-medium">TBD</span>
    : <span className="text-slate-300 text-xs">—</span>;
}

/**
 * A text cell. One line by default; click it and it expands to the full text,
 * click again and it collapses (operator order 2026-10-01: every field can
 * expand and contract). The click never opens or closes the row. The full
 * text also stays on hover, as before.
 */
function Prose({ text, className = 'text-xs text-slate-600', empty = '—' }) {
  const [open, setOpen] = useState(false);
  if (text === null || text === undefined || text === '') return <span className="text-slate-300 text-xs">{empty}</span>;
  const t = String(text);
  const toggle = (e) => { e.stopPropagation(); setOpen((v) => !v); };
  return (
    <span
      role="button"
      tabIndex={0}
      title={open ? 'Click to collapse' : t}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e); } }}
      className={`block cursor-pointer ${open ? 'whitespace-pre-wrap break-words' : 'truncate'} ${className}`}
    >{t}</span>
  );
}

function ExpandField({ label, children, wide = false }) {
  const empty = children === null || children === undefined || children === '' || children === '—';
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className={`text-sm leading-relaxed whitespace-pre-wrap ${empty ? 'text-slate-400' : 'text-slate-700'}`}>
        {empty ? '—' : children}
      </div>
    </div>
  );
}

export default function ActivityTable({
  activities,
  loading,
  emptyText = 'No activities.',
  onViewRecord,
  onViewMeasurements,
  renderActions,       // (act) => extra action buttons for this row
  // Pin the actions column to the right edge of the scroll area so View,
  // Sync, Add months, Expire, Edit and Results are on screen without
  // sideways scrolling (operator report 2026-10-03: Sync hidden in FDR
  // COA). Off by default: the standalone COA renders exactly as before.
  pinActions = false,
  footer = null,
  searchable = true,
  // Bulk selection (operator order 2026-08-19): when onToggleSelect is
  // given, every row grows a leading checkbox and the header checkbox
  // selects everything the filters currently show.
  selectedIds = null,
  onToggleSelect = null,
  onSelectMany = null,
  // Inline result entry for the row expansion (operator order 2026-08-19
  // evening): the department assignee enters the metric right in the grid —
  // no email round-trip required. The server decides who may save.
  renderResultEntry = null,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [q, setQ] = useState('');
  // Rows per page (operator order 2026-10-01): 10 by default so the sideways
  // scrollbar is in reach, with a way to show more. Page resets by clamping.
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  // Field-level filters (operator order 2026-08-19): component, status,
  // type, and frequency narrow the grid alongside the free-text search.
  // Options come from the rows themselves, so they never list dead values.
  // Value columns take MULTIPLE selections (operator order 2026-08-25):
  // each filter holds an array; empty = All. Text columns stay contains.
  const [fComponent, setFComponent] = useState([]);
  const [fStatus, setFStatus] = useState([]);
  const [fType, setFType] = useState([]);
  const [fFreq, setFFreq] = useState([]);
  const [fCfwa, setFCfwa] = useState([]);
  const [fQuarter, setFQuarter] = useState([]);
  const [fRationale, setFRationale] = useState('');
  const [fDescription, setFDescription] = useState('');
  const [fDefDesc, setFDefDesc] = useState('');
  const [fRemediation, setFRemediation] = useState('');
  const [fStart, setFStart] = useState([]);
  const [fCompletion, setFCompletion] = useState([]);
  // Column-title filtering (operator order 2026-08-19 evening): the filter
  // LIVES in the column header — click the title, pick a value. No separate
  // filter bar.
  const [openFilter, setOpenFilter] = useState(null); // column key | null

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const optionsOf = (key) => [...new Set(activities.map((a) => a[key]).filter(Boolean))].sort();
  const componentOptions = useMemo(() => optionsOf('component'), [activities]);
  const statusOptions = useMemo(() => optionsOf('status'), [activities]);
  const typeOptions = useMemo(() => optionsOf('activity_type'), [activities]);
  const freqOptions = useMemo(() => optionsOf('activity_frequency'), [activities]);
  const cfwaOptions = useMemo(() => optionsOf('compliance_or_fwa'), [activities]);
  const quarterOptions = useMemo(() => optionsOf('activity_quarter'), [activities]);
  const monthKey = (v) => { const t = String(v || '').slice(0, 7); return /^\d{4}-\d{2}$/.test(t) ? t : null; };
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthLabel = (k) => `${MONTH_NAMES[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;
  const monthsOf = (key) => [...new Set(activities.map((a) => monthKey(a[key])).filter(Boolean))].sort();
  const startOptions = useMemo(() => monthsOf('activity_start_date'), [activities]);
  const completionOptions = useMemo(() => monthsOf('activity_completion_date'), [activities]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return activities.filter((a) => {
      if (fComponent.length && !fComponent.includes(a.component)) return false;
      if (fStatus.length && !fStatus.includes(a.status)) return false;
      if (fType.length && !fType.includes(a.activity_type)) return false;
      if (fFreq.length && !fFreq.includes(a.activity_frequency)) return false;
      if (fCfwa.length && !fCfwa.includes(a.compliance_or_fwa)) return false;
      if (fQuarter.length && !fQuarter.includes(a.activity_quarter)) return false;
      if (fRationale && !String(a.activity_rationale || '').toLowerCase().includes(fRationale.toLowerCase())) return false;
      if (fDescription && !String(a.activity_description || '').toLowerCase().includes(fDescription.toLowerCase())) return false;
      if (fDefDesc && !String(a.description_of_deficiencies || '').toLowerCase().includes(fDefDesc.toLowerCase())) return false;
      if (fRemediation && !String(a.remediation_activities || '').toLowerCase().includes(fRemediation.toLowerCase())) return false;
      if (fStart.length && !fStart.includes(monthKey(a.activity_start_date))) return false;
      if (fCompletion.length && !fCompletion.includes(monthKey(a.activity_completion_date))) return false;
      if (!needle) return true;
      return [
        a.component, a.entity_name, a.activity_type, a.activity_description,
        a.activity_rationale, a.description_of_deficiencies, a.status,
        ...(a.departments || []).map((d) => d.name),
      ].filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
  }, [activities, q, fComponent, fStatus, fType, fFreq, fCfwa, fQuarter, fRationale, fDescription, fDefDesc, fRemediation, fStart, fCompletion]);
  const anyFilter = q.trim() || fComponent.length || fStatus.length || fType.length || fFreq.length || fCfwa.length || fQuarter.length || fRationale || fDescription || fDefDesc || fRemediation || fStart.length || fCompletion.length;
  const pageCount = pageSize === Infinity ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = pageSize === Infinity ? 0 : safePage * pageSize;
  const pageRows = pageSize === Infinity ? filtered : filtered.slice(pageStart, pageStart + pageSize);

  // The full CMS universe layout, one compact line per row. Widths are fixed
  // per column so no field sprawls; the grid scrolls horizontally as a unit.
  const selectable = typeof onToggleSelect === 'function';
  const COLS = [
    ...(selectable ? [{ key: 'select', label: '', w: 36 }] : []),
    { key: 'chevron', label: '', w: 36 },
    { key: 'component', label: 'A · Component', w: 250 },
    { key: 'status', label: 'Status', w: 88 },
    { key: 'activity_type', label: 'B · Type', w: 92 },
    { key: 'compliance_or_fwa', label: 'C · C/FWA', w: 88 },
    { key: 'activity_frequency', label: 'D · Freq', w: 88 },
    { key: 'quarter', label: 'Qtr', w: 56 },
    { key: 'rationale', label: 'E · Rationale', w: 210 },
    { key: 'description', label: 'F · Description', w: 230 },
    { key: 'start', label: 'G · Start', w: 100 },
    { key: 'completion', label: 'H · Completion', w: 110 },
    { key: 'deficiencies', label: 'I · Defs', w: 70 },
    { key: 'result', label: 'Result', w: 128 },
    { key: 'def_desc', label: 'J · Deficiencies', w: 230 },
    { key: 'remediation', label: 'Remediation', w: 190 },
    { key: 'cap', label: 'K · CAP', w: 68 },
    { key: 'shared', label: 'L · Shared', w: 100 },
    { key: 'actions', label: '', w: 170 },
  ];
  const totalW = COLS.reduce((sum, c) => sum + c.w, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
      {searchable && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200/60 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search component, entity, description…"
              className="w-64 max-w-full rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-700 bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors"
            />
            {anyFilter && (
              <button type="button"
                onClick={() => { setQ(''); setFComponent([]); setFStatus([]); setFType([]); setFFreq([]); setFCfwa([]); setFQuarter([]); setFRationale(''); setFDescription(''); setFDefDesc(''); setFRemediation(''); setFStart([]); setFCompletion([]); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline whitespace-nowrap">
                Clear
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400 whitespace-nowrap">
            {filtered.length} of {activities.length} entr{activities.length === 1 ? 'y' : 'ies'} · click a row to expand
          </span>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-200/60 bg-slate-50/60 text-xs text-slate-600 flex-wrap">
          <span>Showing {pageStart + 1}–{Math.min(pageStart + pageRows.length, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5">Rows per page
              <select value={pageSize === Infinity ? 'all' : String(pageSize)} onChange={(e) => { setPageSize(e.target.value === 'all' ? Infinity : Number(e.target.value)); setPage(0); }} className="border border-slate-300 rounded-lg px-2 py-1 bg-white text-xs">
                <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option><option value="all">All</option>
              </select>
            </label>
            <button type="button" onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0} className="px-2.5 py-1 rounded-lg border border-slate-300 bg-white disabled:opacity-40" aria-label="Previous page">‹</button>
            <span className="tabular-nums">Page {safePage + 1} of {pageCount}</span>
            <button type="button" onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))} disabled={safePage >= pageCount - 1} className="px-2.5 py-1 rounded-lg border border-slate-300 bg-white disabled:opacity-40" aria-label="Next page">›</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="table-fixed text-sm" style={{ width: totalW, minWidth: '100%' }}>
          <colgroup>
            {COLS.map((c) => <col key={c.key} style={{ width: c.w }} />)}
          </colgroup>
          <thead>
            <tr className="bg-slate-50/70 border-b border-slate-200/60 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              {COLS.map((c) => {
                const FILTERABLE = {
                  component: { v: fComponent, set: setFComponent, opts: componentOptions },
                  status: { v: fStatus, set: setFStatus, opts: statusOptions },
                  activity_type: { v: fType, set: setFType, opts: typeOptions },
                  activity_frequency: { v: fFreq, set: setFFreq, opts: freqOptions },
                  compliance_or_fwa: { v: fCfwa, set: setFCfwa, opts: cfwaOptions },
                  quarter: { v: fQuarter, set: setFQuarter, opts: quarterOptions },
                  rationale: { v: fRationale, set: setFRationale, kind: 'text' },
                  description: { v: fDescription, set: setFDescription, kind: 'text' },
                  def_desc: { v: fDefDesc, set: setFDefDesc, kind: 'text' },
                  remediation: { v: fRemediation, set: setFRemediation, kind: 'text' },
                  start: { v: fStart, set: setFStart, opts: startOptions, labelOf: monthLabel },
                  completion: { v: fCompletion, set: setFCompletion, opts: completionOptions, labelOf: monthLabel },
                };
                const f = FILTERABLE[c.key];
                return (
                <th key={c.key} className={`py-2.5 px-3 font-semibold whitespace-nowrap relative${pinActions && c.key === 'actions' ? ' sticky right-0 z-10 bg-slate-50' : ''}`}>
                  {c.key === 'select' ? (
                    <input
                      type="checkbox"
                      aria-label="Select everything shown"
                      title="Select everything the filters currently show"
                      checked={filtered.length > 0 && filtered.every((a) => selectedIds?.has(a.id))}
                      onChange={() => onSelectMany && onSelectMany(filtered.map((a) => a.id), !filtered.every((a) => selectedIds?.has(a.id)))}
                      className="h-3.5 w-3.5 accent-orange-600 cursor-pointer"
                    />
                  ) : f ? (() => {
                    // Value columns hold ARRAYS (multi-select); text columns a string.
                    const isMulti = Array.isArray(f.v);
                    const isActive = isMulti ? f.v.length > 0 : !!f.v;
                    const activeLabel = !isActive ? '' : isMulti
                      ? f.v.map((x) => (f.labelOf ? f.labelOf(x) : x)).join(', ')
                      : `contains "${f.v}"`;
                    return (
                    <>
                      <button type="button"
                        onClick={() => setOpenFilter(openFilter === c.key ? null : c.key)}
                        title={isActive ? `Filtered: ${activeLabel} — click to change` : `Filter by ${c.label.replace(/^[A-L] · /, '')}`}
                        className={`inline-flex items-center gap-1 uppercase tracking-wide font-semibold hover:text-slate-800 ${isActive ? 'text-orange-700' : ''}`}>
                        {c.label}
                        <span className="text-[9px]">{isActive ? (isMulti && f.v.length > 1 ? `●${f.v.length}` : '●') : '▾'}</span>
                      </button>
                      {isActive && <span className="block text-[10px] font-medium normal-case tracking-normal text-orange-700 truncate max-w-[200px]" title={activeLabel}>{activeLabel}</span>}
                      {openFilter === c.key && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setOpenFilter(null)} />
                          <div className="absolute left-0 top-full z-30 mt-1 w-56 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1 normal-case tracking-normal">
                            {f.kind === 'text' ? (
                              <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="search"
                                  autoFocus
                                  value={f.v}
                                  onChange={(e) => f.set(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setOpenFilter(null); }}
                                  placeholder="Contains…"
                                  className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 font-normal"
                                />
                                {f.v && (
                                  <button type="button" onClick={() => { f.set(''); setOpenFilter(null); }}
                                    className="mt-1.5 text-[11px] text-slate-500 hover:text-slate-700 underline font-normal">
                                    Clear this filter
                                  </button>
                                )}
                              </div>
                            ) : (
                              <>
                                {/* Tick as many as needed — the list stays open;
                                    All clears. Empty selection = everything. */}
                                <button type="button"
                                  onClick={() => { f.set([]); setOpenFilter(null); }}
                                  className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${f.v.length === 0 ? 'text-orange-700 font-semibold' : 'text-slate-600'}`}>
                                  All
                                </button>
                                {f.opts.map((o) => {
                                  const on = f.v.includes(o);
                                  return (
                                    <button key={o} type="button"
                                      onClick={() => f.set(on ? f.v.filter((x) => x !== o) : [...f.v, o])}
                                      className={`flex w-full items-center gap-2 text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${on ? 'text-orange-700 font-semibold' : 'text-slate-700'}`}
                                      title={f.labelOf ? f.labelOf(o) : o}>
                                      <span className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[9px] ${on ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white text-transparent'}`}>✓</span>
                                      <span className="truncate">{f.labelOf ? f.labelOf(o) : o}</span>
                                    </button>
                                  );
                                })}
                                {f.v.length > 0 && (
                                  <button type="button" onClick={() => setOpenFilter(null)}
                                    className="block w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 border-t border-slate-100 mt-1">
                                    Done · {f.v.length} selected
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </>
                    );
                  })() : c.label}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={COLS.length} className="py-12 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={COLS.length} className="py-12 text-center text-slate-500">{anyFilter ? 'Nothing matches your filters.' : emptyText}</td></tr>
            )}
            {!loading && pageRows.map((act) => {
              const isOpen = expanded.has(act.id);
              const deptNames = (act.departments || []).map((d) => d.name).join(', ');
              return (
                <React.Fragment key={act.id}>
                  <tr
                    onClick={() => toggle(act.id)}
                    className={`cursor-pointer border-b transition-colors ${isOpen ? 'bg-orange-50/50 border-orange-100' : 'border-slate-100 even:bg-slate-50/30 hover:bg-orange-50/30'}`}
                  >
                    {selectable && (
                      <td className="py-2 px-3 align-middle" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${act.component || 'activity'}`}
                          checked={!!selectedIds?.has(act.id)}
                          onChange={() => onToggleSelect(act.id)}
                          className="h-3.5 w-3.5 accent-orange-600 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="py-2 px-3 align-middle">
                      <span className={`inline-block text-slate-400 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`} aria-hidden>&#9656;</span>
                    </td>
                    <td className="py-2 px-3 align-middle overflow-hidden">
                      <Prose text={act.component} className="font-medium text-slate-800 text-sm" />
                      {act.entity_name && <Prose text={act.entity_name} className="text-[11px] text-slate-500" />}
                    </td>
                    <td className="py-2 px-3 align-middle"><StatusChip act={act} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.activity_type} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.compliance_or_fwa} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.activity_frequency} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.activity_quarter} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.activity_rationale} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.activity_description} /></td>
                    <td className="py-2 px-3 align-middle text-slate-600 whitespace-nowrap tabular-nums text-xs">{cmsDate(act.activity_start_date) || '—'}</td>
                    <td className="py-2 px-3 align-middle text-slate-600 whitespace-nowrap tabular-nums text-xs">{cmsDate(act.activity_completion_date) || 'TBD'}</td>
                    <td className="py-2 px-3 align-middle"><DefBadge value={act.number_of_deficiencies} /></td>
                    <td className="py-2 px-3 align-middle"><ResultCell act={act} onOpen={onViewMeasurements} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.description_of_deficiencies} /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden">
                      {/* A scored MET row with no remediation text says so plainly —
                          the benchmark was met, so there is nothing to remediate. */}
                      {act.monitoring_status === 'met' && !String(act.remediation_activities || '').trim()
                        ? <span className="text-xs text-emerald-700">None required — benchmark met</span>
                        : <Prose text={act.remediation_activities} />}
                    </td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.corrective_action_required} empty="TBD" /></td>
                    <td className="py-2 px-3 align-middle overflow-hidden"><Prose text={act.activity_results_shared} /></td>
                    <td className={`py-2 px-3 align-middle${pinActions ? ' sticky right-0 z-10 bg-white shadow-[inset_1px_0_0_#e2e8f0]' : ''}`} onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {onViewRecord && (
                          <button type="button" onClick={() => onViewRecord(act)} className="text-orange-600 font-medium hover:text-orange-700 text-xs whitespace-nowrap" title="Read this record in the CMS COA layout (Columns A–L)">View</button>
                        )}
                        {renderActions && renderActions(act)}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-slate-100">
                      <td colSpan={COLS.length} className="bg-slate-50/60 px-6 py-5">
                        <div className="grid md:grid-cols-2 gap-x-10 gap-y-4 max-w-5xl">
                          <ExpandField label="E — Activity rationale" wide>{act.activity_rationale}</ExpandField>
                          <ExpandField label="F — Activity description" wide>{act.activity_description}</ExpandField>
                          <ExpandField label="J — Description of deficiencies" wide>{act.description_of_deficiencies}</ExpandField>
                          <ExpandField label="Remediation activities" wide>{act.monitoring_status === 'met' && !String(act.remediation_activities || '').trim() ? 'None required — benchmark met' : act.remediation_activities}</ExpandField>
                          <ExpandField label="K — Corrective action required">{act.corrective_action_required}</ExpandField>
                          <ExpandField label="L — Results shared">{act.activity_results_shared}</ExpandField>
                          <ExpandField label="Departments">{deptNames}</ExpandField>
                          <ExpandField label="Quarter">{act.activity_quarter}</ExpandField>
                          <ExpandField label="CFR references">{act.cfr_references}</ExpandField>
                          <ExpandField label="Chapter references">{act.chapter_references}</ExpandField>
                          {act.status === 'expired' && (
                            <ExpandField label="Expired" wide>
                              {[act.expired_at ? new Date(act.expired_at).toLocaleDateString() : null, act.expired_by ? `by ${act.expired_by}` : null, act.expire_reason ? `— ${act.expire_reason}` : null].filter(Boolean).join(' ')}
                            </ExpandField>
                          )}
                        </div>
                        {renderResultEntry && renderResultEntry(act)}
                        {onViewRecord && (
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => onViewRecord(act)}
                              className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                            >
                              Open full CMS record
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}
